/**
 * The toolbar's sort menu: the handful of orderings an operator actually asks
 * for, worded as intentions rather than as "column plus direction".
 *
 * It exists alongside the sortable column heads, and is not made redundant by
 * them. A head answers "order by THIS column" once you already know which column
 * that is; the menu answers "show me the new companies first", which is the
 * question a salesperson actually arrives with - and it stays visible even when
 * the column it orders by has been hidden.
 *
 * Both write the same two pieces of state, so whichever one is used, the other
 * shows the result: the menu's trigger reads back a sort picked from a head, and
 * a head shows its arrow for a sort picked from the menu.
 */
import type { ArOrder, ArSort } from "../../../lib/ariregister";
import { AR_COLUMNS } from "./columns";

/** One menu row, as `sort:order`. */
export const sortValue = (sort: ArSort, order: ArOrder): string => `${sort}:${order}`;

/** Every sort key the table can produce - the source of truth is the column
 *  list, so a column that loses its sort key cannot be reached from a stale
 *  preset either. */
const SORTABLE = new Set<string>(AR_COLUMNS.flatMap((c) => (c.sort ? [c.sort] : [])));

/** A `sort:order` string back into the two values, or null if it names a column
 *  this table cannot sort by. */
export function parseSortValue(value: string): { sort: ArSort; order: ArOrder } | null {
  const [sort, order] = value.split(":");
  if (!sort || !SORTABLE.has(sort)) return null;
  return { sort: sort as ArSort, order: order === "desc" ? "desc" : "asc" };
}

/** The presets, in the order they are offered. Freshness first: "which of these
 *  companies is new" is the most common reason to reorder this list at all. */
const PRESETS: { sort: ArSort; order: ArOrder; key: string }[] = [
  // First, because it is the whole reason the prospecting list exists: the
  // companies that need web work most and can pay for it.
  { sort: "webNeed", order: "desc", key: "webNeedDesc" },
  { sort: "registered", order: "desc", key: "newest" },
  { sort: "registered", order: "asc", key: "oldest" },
  { sort: "name", order: "asc", key: "nameAsc" },
  { sort: "name", order: "desc", key: "nameDesc" },
  { sort: "revenue", order: "desc", key: "revenueDesc" },
  { sort: "profit", order: "desc", key: "profitDesc" },
  { sort: "employees", order: "desc", key: "employeesDesc" },
  { sort: "status", order: "asc", key: "status" },
];

export interface SortOption {
  value: string;
  label: string;
}

/**
 * The menu rows for the sort in force.
 *
 * A sort picked from a column head is usually not one of the presets (there are
 * thirteen sortable columns and eight presets), and a menu that cannot show the
 * current state would fall back to its first row and claim the list is sorted by
 * something it is not. So an off-menu sort is prepended as its own row, named
 * after the column and the direction it is actually in.
 */
export function sortOptions(
  sort: ArSort,
  order: ArOrder,
  t: (key: string, vars?: Record<string, string | number>) => string,
): SortOption[] {
  const rows = PRESETS.map((p) => ({
    value: sortValue(p.sort, p.order),
    label: t(`ariregister.sort.${p.key}`),
  }));

  const current = sortValue(sort, order);
  if (rows.some((r) => r.value === current)) return rows;

  const column = AR_COLUMNS.find((c) => c.sort === sort);
  const label = column ? t(`ariregister.col.${column.label}`) : sort;
  return [{ value: current, label: `${label} ${order === "asc" ? "↑" : "↓"}` }, ...rows];
}
